import React, { useContext } from 'react'
import { Row, Container, Button } from 'react-bootstrap'
import { DefaultSpinner } from './Spinners'
import * as webeid from '../../utils/web-eid/web-eid'
import { useTranslation } from 'react-i18next'
import httpClient from '../../services/httpClient'
import { IdCardHelpText } from './IdCardContentBlock'
import SigningContext from '../../contexts/SigningContext'
import { appInsights } from '../../appInsights'

// The DigiDoc4j wrapper always returns a SHA-256 digest to sign
const hashFunction = 'SHA-256'

function IdCardSignBLock() {
    const { t, i18n } = useTranslation()
    const [waiting, setWaiting] = React.useState(false)
    const { signingRequest, setError, setSigned } = useContext(SigningContext)

    const id = signingRequest?.id ?? ''

    async function initiateSign() {
        setError(undefined)

        setWaiting(true)

        const lang = i18n.language.substring(0, 2)

        try {
            const { certificate } = await webeid.getSigningCertificate({ lang })

            // The backend works with hex, Web eID with base64
            const signInitiate = await httpClient.idCardSignInitiate(
                id,
                base64ToHex(certificate)
            )

            const { signature } = await webeid.sign(
                certificate,
                hexToBase64(signInitiate?.digest ?? ''),
                hashFunction,
                { lang }
            )

            await completeSigning(
                base64ToHex(signature),
                id,
                signInitiate?.containerID || ''
            )
        } catch (exception) {
            appInsights.trackException({ exception: exception as Error })

            const { code } = exception as webeid.WebEidError
            setError(
                code
                    ? new Error(
                          t(`IdCardSignError_${code}`, t('IdCardSignError'))
                      )
                    : (exception as Error)
            )
            setWaiting(false)
        }
    }

    async function completeSigning(
        signatureValue: string,
        signingRequestId: string,
        containerId: string
    ) {
        const data = await httpClient.idCardSignComplete(
            signingRequestId,
            signatureValue,
            containerId
        )

        setWaiting(false)

        if (data && data.url) {
            window.location.replace(data.url)
        } else {
            setSigned()
        }
    }

    return (
        <Row>
            <Container id="IdCardSignBlock">
                {waiting ? (
                    <DefaultSpinner />
                ) : (
                    <>
                        <IdCardHelpText />
                        <Button onClick={initiateSign} variant="primary">
                            {t('Proceed')}
                        </Button>
                    </>
                )}
            </Container>
        </Row>
    )
}

export default IdCardSignBLock

function base64ToHex(base64: string) {
    return Array.from(atob(base64), (char) =>
        char.charCodeAt(0).toString(16).padStart(2, '0')
    ).join('')
}

function hexToBase64(hex: string) {
    const bytes = hex.match(/.{2}/g) ?? []
    return btoa(
        bytes.map((byte) => String.fromCharCode(parseInt(byte, 16))).join('')
    )
}
